import type { characters } from "../../video/characters.js";
import type { ItemWriting } from "./yufa-written-item.js";
import type { MeaningOption } from "./yufa-meaning.js";
import { recordOf, writingOf } from "./yufa-writing-read.js";

export const visualCast = {
  ziming: { simplified: "子明", traditional: "子明" },
  xinyu: { simplified: "新雨", traditional: "新雨" },
  xiaomei: { simplified: "小美", traditional: "小美" },
} as const satisfies Partial<Record<keyof typeof characters, ItemWriting>>;

interface SceneEntity {
  readonly id: string;
  readonly name: ItemWriting;
}

/** Geometry and answer claims share the measurements in this scene. */
export interface HeightScene {
  readonly kind: "height";
  readonly asset: "height-v1";
  readonly unit: "cm";
  readonly entities: readonly (SceneEntity & {
    readonly character: keyof typeof visualCast;
    readonly height: number;
  })[];
  readonly choices: readonly {
    readonly subject: string;
    readonly other: string;
    readonly relation: "taller" | "shorter";
  }[];
}

/** Panel order is chronological in every presentation. States use explicit labels. */
export interface ChangeScene {
  readonly kind: "change";
  readonly asset: "door-v1" | "lamp-v1";
  readonly entity: SceneEntity;
  readonly panels: readonly {
    readonly order: number;
    readonly time: "Before" | "Now";
    readonly state: "open" | "closed" | "on" | "off";
  }[];
  readonly choices: readonly {
    readonly state: "open" | "closed" | "on" | "off";
  }[];
}

export type VisualScene = HeightScene | ChangeScene;

function entityOf(value: unknown): SceneEntity | undefined {
  const record = recordOf(value);
  const name = writingOf(record?.["name"]);
  return typeof record?.["id"] === "string" &&
    /^[a-z][a-z0-9-]{0,39}$/u.test(record["id"]) &&
    name !== undefined &&
    [name.simplified, name.traditional].every((text) =>
      /^[\p{Script=Han}]{1,6}$/u.test(text),
    )
    ? { id: record["id"], name }
    : undefined;
}

/** Reject unknown drawing assets, impossible measurements and ambiguous panel sequences. */
export function visualSceneOf(value: unknown): VisualScene | undefined {
  const record = recordOf(value);
  if (
    record === undefined ||
    !Array.isArray(record["choices"]) ||
    record["choices"].length < 2 ||
    record["choices"].length > 4
  ) {
    return undefined;
  }
  if (
    record["kind"] === "height" &&
    record["asset"] === "height-v1" &&
    record["unit"] === "cm"
  ) {
    const entities = Array.isArray(record["entities"])
      ? record["entities"].map((value: unknown) => {
          const entity = entityOf(value);
          const height = recordOf(value)?.["height"];
          const character = recordOf(value)?.["character"];
          const cast =
            typeof character === "string" &&
            Object.hasOwn(visualCast, character)
              ? visualCast[character as keyof typeof visualCast]
              : undefined;
          return entity !== undefined &&
            cast !== undefined &&
            entity.name.simplified === cast.simplified &&
            entity.name.traditional === cast.traditional &&
            typeof height === "number" &&
            Number.isInteger(height) &&
            height >= 100 &&
            height <= 200
            ? {
                ...entity,
                character: character as keyof typeof visualCast,
                height,
              }
            : undefined;
        })
      : [];
    if (
      entities.length !== 2 ||
      !entities.every((entity) => entity !== undefined) ||
      new Set(entities.map((entity) => entity.id)).size !== 2 ||
      new Set(entities.map((entity) => entity.height)).size !== 2 ||
      ["simplified", "traditional"].some(
        (script) =>
          new Set(
            entities.map((entity) => entity.name[script as keyof ItemWriting]),
          ).size !== 2,
      )
    ) {
      return undefined;
    }
    const choices = record["choices"].map(
      (value: unknown): HeightScene["choices"][number] | undefined => {
        const claim = recordOf(value);
        return typeof claim?.["subject"] === "string" &&
          typeof claim["other"] === "string" &&
          claim["subject"] !== claim["other"] &&
          entities.some((entity) => entity.id === claim["subject"]) &&
          entities.some((entity) => entity.id === claim["other"]) &&
          (claim["relation"] === "taller" || claim["relation"] === "shorter")
          ? {
              subject: claim["subject"],
              other: claim["other"],
              relation: claim["relation"],
            }
          : undefined;
      },
    );
    return choices.every((choice) => choice !== undefined)
      ? { kind: "height", asset: "height-v1", unit: "cm", entities, choices }
      : undefined;
  }
  if (
    record["kind"] === "change" &&
    (record["asset"] === "door-v1" || record["asset"] === "lamp-v1")
  ) {
    const entity = entityOf(record["entity"]);
    const states =
      record["asset"] === "door-v1" ? ["open", "closed"] : ["on", "off"];
    const panels = record["panels"];
    const names = record["asset"] === "door-v1" ? ["门", "門"] : ["灯", "燈"];
    if (
      entity === undefined ||
      entity.name.simplified !== names[0] ||
      entity.name.traditional !== names[1] ||
      !Array.isArray(panels) ||
      panels.length !== 2 ||
      !panels.every((value: unknown, at: number) => {
        const panel = recordOf(value);
        return (
          panel?.["order"] === at &&
          panel["time"] === (at === 0 ? "Before" : "Now") &&
          typeof panel["state"] === "string" &&
          states.includes(panel["state"])
        );
      }) ||
      recordOf(panels[0])?.["state"] === recordOf(panels[1])?.["state"] ||
      !record["choices"].every((value: unknown) => {
        const state = recordOf(value)?.["state"];
        return typeof state === "string" && states.includes(state);
      })
    ) {
      return undefined;
    }
    return {
      kind: "change",
      asset: record["asset"],
      entity,
      panels: panels as ChangeScene["panels"],
      choices: record["choices"] as ChangeScene["choices"],
    };
  }
  return undefined;
}

/** A small checked vocabulary binds each option's two scripts to its factual claim. */
export function visualChoice(
  scene: VisualScene,
  at: number,
): { writing: ItemWriting; matches: boolean } {
  if (scene.kind === "height") {
    const claim = scene.choices[at]!;
    const subject = scene.entities.find(
      (entity) => entity.id === claim.subject,
    )!;
    const other = scene.entities.find((entity) => entity.id === claim.other)!;
    const adjective = claim.relation === "taller" ? "高" : "矮";
    return {
      writing: {
        simplified: `${subject.name.simplified}比${other.name.simplified}${adjective}。`,
        traditional: `${subject.name.traditional}比${other.name.traditional}${adjective}。`,
      },
      matches:
        claim.relation === "taller"
          ? subject.height > other.height
          : subject.height < other.height,
    };
  }
  const state = scene.choices[at]!.state;
  const word = {
    open: ["开", "開"],
    closed: ["关", "關"],
    on: ["亮", "亮"],
    off: ["灭", "滅"],
  }[state];
  return {
    writing: {
      simplified: `${scene.entity.name.simplified}${word[0]}了。`,
      traditional: `${scene.entity.name.traditional}${word[1]}了。`,
    },
    matches: state === scene.panels[1]!.state,
  };
}

/** A key must include every true claim, with the exact Chinese sentence in each script. */
export function visualAnswersMatch(
  scene: VisualScene,
  options: readonly MeaningOption[],
  accepted: readonly number[],
): boolean {
  return (
    scene.choices.length === options.length &&
    options.every((option, at) => {
      const claim = visualChoice(scene, at);
      return (
        option.language === "zh" &&
        option.writing.simplified === claim.writing.simplified &&
        option.writing.traditional === claim.writing.traditional &&
        accepted.includes(at) === claim.matches
      );
    })
  );
}
